// @ts-nocheck
import React from "react";
import { motion } from "framer-motion";
import { Notifications } from "@/integration/application/interfaces/notification.interface.ts";
import { IParamsInivteNotification } from "@integration/application/interfaces/IParamsInivteNotification.ts";
import NotificationHeader from "./components/NotificationHeader";
import NotificationContent from "./components/NotificationContent";
import NotificationActions from "./components/NotificationActions";
import NotificationFooter from "./components/NotificationFooter";
import { shouldShowActions } from "./utils/types";

/**
 * Props for the NotificationCard component
 */
interface NotificationCardProps {
  notification: Notifications;
  markAsRead?: (id: string) => void;
  deleteNotification?: (id: string) => void;
  acceptionInvitationNotification?: (params: IParamsInivteNotification) => Promise<void>;
  declineInvitationNotification?: (params: IParamsInivteNotification) => Promise<void>;
  compact?: boolean; // For compact view in NotificationButton
}

/**
 * NotificationCard component
 * Displays a single notification with actions
 * 
 * This component has been refactored to use smaller, focused components:
 * - NotificationHeader: Displays the notification title and read/unread indicator
 * - NotificationContent: Displays the content of the notification based on its type
 * - NotificationActions: Displays accept/decline buttons for invitation notifications
 * - NotificationFooter: Displays the timestamp and mark as read/delete buttons
 */
const NotificationCard: React.FC<NotificationCardProps> = React.memo(({
  notification,
  markAsRead,
  deleteNotification,
  acceptionInvitationNotification,
  declineInvitationNotification,
  compact = false,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      key={notification?.id}
      className={`${compact ? 'p-3' : 'p-6'} rounded-lg transition-all duration-200 border backdrop-blur-sm relative overflow-hidden ${
        notification.isRead
          ? "bg-gradient-to-b from-gray-800/60 to-gray-900/60 border-gray-700/50"
          : "bg-gradient-to-b from-gray-800/40 to-gray-900/40 border-red-500/20 hover:border-red-500/30"
      }`}
      whileHover={{ y: -2, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
    >
      {/* Background effects */}
      <div className="absolute inset-0 bg-gradient-to-r from-red-600/5 via-red-600/5 to-transparent opacity-50"></div>
      <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 rounded-full filter blur-3xl -translate-y-1/2 translate-x-1/2"></div>
      <div className="absolute bottom-0 left-0 w-32 h-32 bg-red-500/5 rounded-full filter blur-3xl translate-y-1/2 -translate-x-1/2"></div>

      {/* Card content */}
      <div className="relative z-10">
        {/* Header */}
        <NotificationHeader 
          notification={notification} 
          compact={compact} 
        />

        {/* Content */}
        {notification?.body && (
          <div className="flex-1">
            <NotificationContent 
              notification={notification} 
              compact={compact} 
            />

            {/* Action buttons for invitations */}
            {shouldShowActions(notification) && 
             acceptionInvitationNotification && 
             declineInvitationNotification && (
              <NotificationActions 
                notification={notification} 
                onAccept={acceptionInvitationNotification}
                onDecline={declineInvitationNotification}
                compact={compact}
              />
            )}

            {/* Footer with timestamp and actions */}
            <NotificationFooter 
              notification={notification}
              onMarkAsRead={markAsRead}
              onDelete={deleteNotification}
              compact={compact}
            />
          </div>
        )}
      </div>
    </motion.div>
  );
});

NotificationCard.displayName = "NotificationCard";

export default NotificationCard;
